import {
  CirclesThreeIcon,
  customTv,
  FilesIcon,
  GitLogo,
  StackletLogo,
  Tooltip,
} from "@stacklet/ui";

const rbacChipStyles = customTv({
  base: "flex h-auto w-fit items-center justify-center gap-xs rounded-1 border border-legacy-navy-L70 bg-transparent px-xxs py-3xs",
  variants: {
    variant: {
      column:
        "max-w-[14.0625rem] text-label-extra-small-strong text-legacy-neutrals-ash",
      role: "min-w-auto text-label-small-strong text-legacy-neutrals-eerieBlack",
      // Rounded pill used in the role assignment table scope column: neutral
      // fill, no border, icon + visible label.
      scope:
        "rounded-full border-0 bg-background-secondary pl-xs pr-lg text-label-medium text-text-primary",
    },
  },
  defaultVariants: {
    variant: "column",
  },
});

type RbacChipKey =
  | "column-system"
  | "column-policy-collection"
  | "column-account-group"
  | "column-repository";

const rbacChipVariantMap: Record<
  RbacChipKey,
  { icon: React.ReactNode; label?: string }
> = {
  "column-system": {
    icon: <StackletLogo className="size-3.5 shrink-0" />,
    label: "System",
  },
  "column-policy-collection": {
    icon: <FilesIcon className="size-3.5 shrink-0" />,
  },
  "column-account-group": {
    icon: <CirclesThreeIcon className="size-3.5 shrink-0" />,
  },
  "column-repository": {
    icon: <GitLogo className="size-3.5 shrink-0" />,
  },
};

export const typenameChipMap: Record<string, RbacChipKey> = {
  UserGroup: "column-account-group",
  AccountGroup: "column-account-group",
  System: "column-system",
  RoleScope: "column-system",
  PolicyCollection: "column-policy-collection",
  RepositoryConfig: "column-repository",
};

const rbacChipTooltipMap: Record<RbacChipKey, string> = {
  "column-system": "System",
  "column-policy-collection": "Policy Collection",
  "column-account-group": "Account Group",
  "column-repository": "Repository",
};

interface RbacChipProps {
  rbacType?: RbacChipKey;
  variant?: "column" | "role" | "scope";
  label?: React.ReactNode;
  className?: string;
}

export function RbacChip({
  rbacType,
  variant,
  label,
  className,
}: RbacChipProps) {
  if (rbacType) {
    const chip = rbacChipVariantMap[rbacType];
    if (!chip) return null;

    const displayLabel = label ?? chip.label ?? "";
    const chipContent = (
      <span
        className={rbacChipStyles({ variant: variant ?? "column", className })}
      >
        {chip.icon}
        {displayLabel ? <span>{displayLabel}</span> : null}
      </span>
    );

    // Scope pills already show a visible label, so the redundant type tooltip
    // is omitted.
    if (variant === "scope") {
      return chipContent;
    }

    return (
      <Tooltip
        buttonProps={{
          "aria-label": rbacChipTooltipMap[rbacType],
          className:
            "inline-flex cursor-default border-none bg-transparent p-0 outline-none",
        }}
        title={rbacChipTooltipMap[rbacType]}
      >
        {chipContent}
      </Tooltip>
    );
  }

  return (
    <span className={rbacChipStyles({ variant, className })}>{label}</span>
  );
}
